import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { computeKeepAliveInclude } from '../flow-core';
import { StepFlowContext } from './StepFlowContext';
import type {
  StepFlowInstance,
  StepFlowProps,
  StepFlowStepComponent,
  StepFlowStepStatus,
} from './types';
import { validateStepConfig } from './utils';

const StepFlowComponent = forwardRef<StepFlowInstance<any>, StepFlowProps>(
  (props, ref) => {
    const {
      steps,
      defaultStatus,
      cacheStateOnReset = false,
      historyMode = false,
      keepAlive = false,
      onProceed,
      onDrawback,
      onChange,
      onReset,
      children,
    } = props;

    // Validate configuration
    useEffect(() => {
      validateStepConfig(steps);
    }, [steps]);

    const [stepHistoryStack, setStepHistoryStack] = useState<
      StepFlowStepComponent[]
    >([]);
    const defaultStatusValue = (defaultStatus ??
      steps[0]?.status) as StepFlowStepStatus;
    const [status, setStatus] =
      useState<StepFlowStepStatus>(defaultStatusValue);
    const [stepValues, setStepValues] = useState<
      Record<StepFlowStepStatus, any[]>
    >({});
    const [prevPassedValue, setPrevPassedValue] = useState<any[]>([]);
    const [internalSteps, setInternalSteps] = useState(steps);
    const [isPreventDefaultFlow, setIsPreventDefaultFlow] = useState(false);

    useEffect(() => {
      setInternalSteps(steps);
    }, [steps]);

    // Compute keepAlive configuration
    const keepAliveInclude = useMemo(() => {
      return computeKeepAliveInclude(internalSteps, keepAlive);
    }, [internalSteps, keepAlive]);

    const handleReset = useCallback(() => {
      setStepHistoryStack([]);
      if (!cacheStateOnReset) {
        setStatus(defaultStatusValue);
      }
      onReset?.();
    }, [cacheStateOnReset, defaultStatusValue, onReset]);

    const handleChangeStatus = useCallback(
      (nextStatus: StepFlowStepStatus, ...args: any[]) => {
        const curStep = internalSteps.find((item) => item.status === status);
        const nextStep = internalSteps.find(
          (item) => nextStatus === item.status,
        );

        if (!nextStep) {
          throw new Error('The status you set is invalid.');
        }

        setIsPreventDefaultFlow(true);
        onChange?.(status, nextStatus, ...args);
        setStatus(nextStatus);

        setPrevPassedValue(args);
        setStepValues((prev) => ({ ...prev, [status]: args }));

        if (curStep) {
          setStepHistoryStack((prev) => [...prev, curStep]);
        }

        setIsPreventDefaultFlow(false);
      },
      [internalSteps, status, onChange],
    );

    const handleChangeCallback = useCallback(
      (
        currentStatus: StepFlowStepStatus,
        nextStatus: StepFlowStepStatus,
        ...args: any[]
      ) => {
        onChange?.(currentStatus, nextStatus, ...args);

        if (!isPreventDefaultFlow) {
          setStatus(nextStatus);
          setPrevPassedValue(args);
          setStepValues((prev) => ({ ...prev, [currentStatus]: args }));
        }

        setIsPreventDefaultFlow(false);
      },
      [onChange, isPreventDefaultFlow],
    );

    const handleProceed = useCallback(
      (...args: any[]) => {
        const curIdx = internalSteps.findIndex(
          (item) => item.status === status,
        );
        const { status: curStatus } = internalSteps[
          curIdx
        ] as StepFlowStepComponent;

        if (curIdx === internalSteps.length - 1) {
          setStepValues((prev) => ({ ...prev, [curStatus]: args }));
          return;
        }

        const nextStatus = internalSteps[curIdx + 1]
          ?.status as StepFlowStepStatus;

        onProceed?.(curStatus, nextStatus, ...args);
        handleChangeCallback(curStatus, nextStatus, ...args);
        setStepHistoryStack((prev) => [
          ...prev,
          internalSteps[curIdx] as StepFlowStepComponent,
        ]);
      },
      [internalSteps, status, onProceed, handleChangeCallback],
    );

    const handleDrawback = useCallback(
      (...args: any[]) => {
        const curIdx = internalSteps.findIndex(
          (item) => item.status === status,
        );
        const { status: curStatus } = internalSteps[
          curIdx
        ] as StepFlowStepComponent;

        if (historyMode && !stepHistoryStack.length && curIdx === 0) return;
        if (!historyMode && curIdx === 0) return;

        const leftStatus = internalSteps[curIdx - 1]
          ?.status as StepFlowStepStatus;
        const toStatus =
          historyMode && stepHistoryStack.length
            ? (stepHistoryStack[stepHistoryStack.length - 1]
                ?.status as StepFlowStepStatus)
            : leftStatus;

        onDrawback?.(curStatus, toStatus, ...args);
        handleChangeCallback(curStatus, toStatus, ...args);

        setStepHistoryStack((prev) => {
          const newStack = [...prev];
          newStack.pop();
          return newStack;
        });
      },
      [
        internalSteps,
        status,
        historyMode,
        stepHistoryStack,
        onDrawback,
        handleChangeCallback,
      ],
    );

    // Expose methods to parent component via ref
    useImperativeHandle(
      ref,
      () => ({
        setStatus: handleChangeStatus,
        reset: handleReset,
        proceed: handleProceed,
        drawback: handleDrawback,
        getStepsValue: () => stepValues,
        getStepValue: (key: StepFlowStepStatus) => stepValues[key] ?? [],
        getStatus: () => status,
      }),
      [
        handleChangeStatus,
        handleReset,
        handleProceed,
        handleDrawback,
        stepValues,
        status,
      ],
    );

    // Context value
    const contextValue = useMemo(
      () => ({
        prevValue: prevPassedValue,
        stepValues,
        proceed: handleProceed,
        drawback: handleDrawback,
        reset: handleReset,
        setStatus: handleChangeStatus,
      }),
      [
        prevPassedValue,
        stepValues,
        handleProceed,
        handleDrawback,
        handleReset,
        handleChangeStatus,
      ],
    );

    // Render current step component
    const renderCurrentStep = useCallback(() => {
      // If custom children render is provided
      if (children) {
        return children(status);
      }

      // If keepAlive is enabled, render all steps that need to be cached
      if (keepAliveInclude.length > 0) {
        return (
          <>
            {internalSteps.map((step) => {
              const isActive = step.status === status;
              const shouldCache =
                step.status && keepAliveInclude.includes(step.status);

              // Only render active step or steps that need to be cached
              if (!isActive && !shouldCache) {
                return null;
              }

              const StepComponent = step.component;
              const stepProps = step.componentProps?.() ?? {};

              const componentElement = (
                <StepComponent
                  key={String(step.status)}
                  onProceed={handleProceed}
                  onDrawback={handleDrawback}
                  onReset={handleReset}
                  onSetStatus={handleChangeStatus}
                  {...stepProps}
                />
              );

              // Use display:none to hide inactive steps while keeping them mounted
              if (shouldCache) {
                return (
                  <div
                    key={step.status}
                    style={{
                      display: isActive ? 'block' : 'none',
                    }}
                  >
                    {componentElement}
                  </div>
                );
              }

              return componentElement;
            })}
          </>
        );
      }

      // When keepAlive is not enabled, only render the current step
      const currentStep = internalSteps.find((item) => item.status === status);
      if (!currentStep) return null;

      const StepComponent = currentStep.component;
      const stepProps = currentStep.componentProps?.() ?? {};

      return (
        <StepComponent
          key={String(status)}
          onProceed={handleProceed}
          onDrawback={handleDrawback}
          onReset={handleReset}
          onSetStatus={handleChangeStatus}
          {...stepProps}
        />
      );
    }, [
      children,
      status,
      internalSteps,
      handleProceed,
      handleDrawback,
      handleReset,
      handleChangeStatus,
      keepAliveInclude,
    ]);

    const stepContent = renderCurrentStep();

    return (
      <StepFlowContext.Provider value={contextValue}>
        {stepContent}
      </StepFlowContext.Provider>
    );
  },
);

StepFlowComponent.displayName = 'StepFlow';

export default StepFlowComponent;
